import React, {useState, useEffect} from 'react';
import {API} from 'aws-amplify';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MuiAlert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';

import {SNACKBAR_DURATION, ENDPOINT} from '../../constants';

import baseStyles from '../../css/Base.module.css';

export default function CopyLessonDialog({isOpen, handleClose, lesson, setActiveTabIndex}) {
  const [name, setName] = useState(lesson?.name ? `Copy of ${lesson.name}` : '');
  const [description, setDescription] = useState(lesson?.description ?? '');
  const [isSnackbarOpen, toggleSnackbar] = useState(false);
  const [isLoading, toggleLoading] = useState(false);

  useEffect(() => {
    if (lesson) {
      setName(lesson?.name ? `Copy of ${lesson.name}` : '');
      setDescription(lesson?.description ?? '');
    }
  }, [lesson]);

  function handleNameChange(event) {
    setName(event.target.value);
  }

  function handleDescriptionChange(event) {
    setDescription(event.target.value);
  }

  function isSaveButtonEnabled() {
    return !!name && !!description;
  }

  // TODO: Not using redux actions here because the loading indicator
  // on the lessons page showing/hiding repeatably. Should think about
  // working around this issue.
  async function handleSave() {
    toggleLoading(true);

    // Create new lesson
    const instructions = lesson?.instructions;
    const lessonCreateBody = {body: {name, description, visibility: 'PRIVATE', ...(instructions && {instructions})}};
    const myLesson = await API.post(ENDPOINT, '/lessons', lessonCreateBody);

    // Copy exercises
    const stepsClone = JSON.parse(JSON.stringify(lesson.steps));
    for (let i = 0; i < stepsClone.length; i += 1) {
      const step = stepsClone[i];
      for (let j = 0; j < step.exercises.length; j += 1) {
        const exerciseID = step.exercises[j];
        const exercise = await API.get(ENDPOINT, `/exercises/${exerciseID}`);
        const createExerciseBody = {
          body: {
            name: exercise.name,
            type: exercise.type,
            description: exercise.description,
            visibility: 'PRIVATE',
            ...(exercise?.instructions && {instructions: exercise.instructions}),
            config: exercise.config,
          },
        };
        const myExercise = await API.post(ENDPOINT, '/exercises', createExerciseBody);
        step.exercises[j] = myExercise._id;
      }
    }

    // Add exercises to new lesson
    const lessonUpdateBody = {body: {steps: stepsClone}};
    await API.patch(ENDPOINT, `/lessons/${myLesson._id}`, lessonUpdateBody);

    handleClose();
    toggleLoading(false);
    toggleSnackbar(true);
    setActiveTabIndex(0); // Back to "My Lessons"
  }

  return (
    <>
      <Snackbar
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={isSnackbarOpen}
        autoHideDuration={SNACKBAR_DURATION}
        onClose={toggleSnackbar}
      >
        <MuiAlert elevation={6} variant="filled" severity="success">
          Lesson saved
        </MuiAlert>
      </Snackbar>
      <Dialog open={isOpen} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>Make a copy for yourself</DialogTitle>
        <DialogContent>
          <div>
            <TextField
              variant="standard"
              value={name ?? ''}
              onChange={handleNameChange}
              label="Name"
              autoFocus
              fullWidth
              required
            />

            <TextField
              variant="standard"
              className={baseStyles['m-t-10']}
              value={description ?? ''}
              onChange={handleDescriptionChange}
              label="Description"
              multiline
              fullWidth
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button className={baseStyles['btn-pill']} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            className={baseStyles['btn-pill']}
            disabled={!isSaveButtonEnabled()}
            onClick={handleSave}
            variant="contained"
            startIcon={isLoading && <CircularProgress size={20} />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
